import { Controller } from '@hotwired/stimulus'
import { applyAllDay } from '../tour_plan_date_all_day'

// records 編集画面の親 form 内にラウンドブロックが描画されるため、入れ子 form を避けて fetch で送信する。
export default class extends Controller {
  static values = {
    requestRoundUrl: String,
    applyBulkUrl: String,
    // Issue #13483: 差し戻し中ラウンドの「調整不要」クローズの送信先と対象ラウンドID。
    closeReturnedUrl: String,
    roundId: Number,
    requestId: Number,
    roundNumber: Number,
    successRedirectUrl: String,
    // #13378: 見学調整ラウンドのみ希望施設数・見学候補日の送信前チェックを行う（リスケ/キャンセルは false）。
    tourArrangement: Boolean,
  }

  static targets = ['confirmModal', 'bulkDatesContainer']

  storageKey () {
    return `caresul.round_bulk_form.${this.requestIdValue}.${this.roundNumberValue}`
  }

  connect () {
    this.restoreBulkInputs()
  }

  // 個別編集 → 一括反映 → 調整依頼 の操作順で個別編集行の値が上書きされない事故を防ぐため、
  // window.confirm ではなく確認モーダルを表示する。モーダル本体は ViewComponent 側で
  // 各行の DB 現在値を全文表示する HTML として埋め込み済み（display: none）。
  requestRound (event) {
    event.preventDefault()
    event.stopPropagation()
    this.openConfirmModal()
  }

  openConfirmModal () {
    if (!this.hasConfirmModalTarget) return
    this.confirmModalTarget.hidden = false
    document.body.classList.add('round-confirm-modal-open')
  }

  // Issue #13483: 差し戻し中ラウンドを「調整不要」としてクローズする。
  // 誤操作防止のため window.confirm で確認してから送信する。クローズ後は再依頼できない
  // （必要になった場合は新規の調整依頼として起こす）ため、その旨も確認文言に含める。
  async closeReturned (event) {
    event.preventDefault()
    event.stopPropagation()

    const message = 'このラウンドを「調整不要」としてクローズします。よろしいですか？\n' +
      'クローズ後はこのラウンドを再依頼できません（必要になった場合は新規に調整依頼を作成してください）。'
    if (!window.confirm(message)) return

    const formData = new FormData()
    formData.append('round_id', this.roundIdValue)
    await this.submit(this.closeReturnedUrlValue, formData)
  }

  cancelConfirm (event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    this.closeConfirmModal()
  }

  closeConfirmModal () {
    if (!this.hasConfirmModalTarget) return
    this.confirmModalTarget.hidden = true
    document.body.classList.remove('round-confirm-modal-open')
  }

  // 確認モーダル内の「この内容で依頼する」ボタンから実際の submit を実行する。
  async confirmRequestRound (event) {
    event.preventDefault()
    event.stopPropagation()

    // #13378: 見学調整ラウンドは「希望施設数」「全グループの見学候補日（新形式）」が
    // 揃っていない限り調整依頼を送信しない。サーバ側でも同条件を検証しているが、
    // CA1 が何を直せばよいか分かるよう、ここでも送信前に警告する。
    const errors = this.collectRequestRoundErrors()
    if (errors.length > 0) {
      this.closeConfirmModal()
      window.alert(errors.join('\n'))
      return
    }

    this.closeConfirmModal()

    const formData = new FormData()
    formData.append('request_id', this.requestIdValue)
    formData.append('round_number', this.roundNumberValue)
    await this.submit(this.requestRoundUrlValue, formData)
  }

  // #13378: 調整依頼の前提条件（希望施設数・見学候補日）の不足を文言の配列で返す。
  // リスケ/キャンセルラウンド（tourArrangement=false）は従来どおりチェックしない。
  collectRequestRoundErrors () {
    if (!this.tourArrangementValue) return []

    const errors = []

    const countInput = this.element.querySelector('[data-desired-facility-count-input]')
    if (countInput && countInput.value.trim() === '') {
      errors.push('希望施設数が未入力です。希望施設数を入力してください。')
    }

    const missingGroups = this.groupsMissingTourPlanDates()
    if (missingGroups.length > 0) {
      errors.push(`見学候補日が未設定のグループがあります（${missingGroups.join('、')}）。全グループに見学候補日を設定してください。`)
    }

    return errors
  }

  // 新形式の見学候補日が未設定のグループ名一覧を返す。確認モーダルの各行が持つ
  // data-has-tour-plan-dates（proposed_date のみは false）を判定に用いる。
  groupsMissingTourPlanDates () {
    if (!this.hasConfirmModalTarget) return []

    const rows = this.confirmModalTarget.querySelectorAll('[data-confirm-row]')
    const missing = []
    rows.forEach((row) => {
      if (row.dataset.hasTourPlanDates !== 'true') {
        missing.push(row.dataset.groupName || 'グループ')
      }
    })
    return missing
  }

  async applyBulk (event) {
    event.preventDefault()
    event.stopPropagation()

    const formData = new FormData()
    formData.append('request_id', this.requestIdValue)
    formData.append('round_number', this.roundNumberValue)

    const inputs = this.bulkInputs()
    inputs.forEach((input) => {
      if (input.type === 'checkbox') {
        if (input.checked) formData.append(input.name, input.value || '1')
      } else if (input.value !== '') {
        formData.append(input.name, input.value)
      }
    })

    this.appendBulkDates(formData)
    this.persistBulkInputs(inputs)
    await this.submit(this.applyBulkUrlValue, formData)
  }

  // C-4 (#13048 CA1-3): 一括反映の候補日リストを bulk_tour_plan_dates[N][start_at|end_at] で送る。
  appendBulkDate (event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    if (!this.hasBulkDatesContainerTarget) return

    const container = this.bulkDatesContainerTarget
    const idx = container.querySelectorAll('[data-bulk-date-row]').length
    const row = document.createElement('div')
    row.className = 'bulk-tour-plan-date-row d-flex align-items-center gap-2 mb-2'
    row.dataset.bulkDateRow = 'true'

    const start = document.createElement('input')
    start.type = 'datetime-local'
    start.className = 'form-control bulk-tour-plan-date-start-at'
    start.dataset.bulkDateInput = 'start_at'
    row.appendChild(start)

    const separator = document.createElement('span')
    separator.textContent = '〜'
    row.appendChild(separator)

    const end = document.createElement('input')
    end.type = 'datetime-local'
    end.className = 'form-control bulk-tour-plan-date-end-at'
    end.dataset.bulkDateInput = 'end_at'
    row.appendChild(end)

    // Issue #13241: 「終日」チェックで 00:00〜23:59 を自動入力する。
    const allDayLabel = document.createElement('label')
    allDayLabel.className = 'ml-2 mb-0 small'
    const allDayCheckbox = document.createElement('input')
    allDayCheckbox.type = 'checkbox'
    allDayCheckbox.className = 'mr-1'
    allDayCheckbox.dataset.action = 'change->admin--ca--request--new-tour-plan--round-block--component#toggleBulkAllDay'
    allDayLabel.appendChild(allDayCheckbox)
    allDayLabel.appendChild(document.createTextNode('終日'))
    row.appendChild(allDayLabel)

    // 追加した候補日行を削除できるように削除ボタンを併設する。
    const remove = document.createElement('button')
    remove.type = 'button'
    remove.className = 'btn btn-outline-danger btn-sm ml-2'
    remove.textContent = '削除'
    remove.dataset.action = 'click->admin--ca--request--new-tour-plan--round-block--component#removeBulkDate'
    row.appendChild(remove)

    container.appendChild(row)
  }

  // 一括反映用の候補日行を削除する（appendBulkDate で追加した行のみが対象）。
  removeBulkDate (event) {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.closest('[data-bulk-date-row]')?.remove()
  }

  // Issue #13241: 一括反映行の「終日」チェックで、その行の開始時刻を 00:00・終了時刻を 23:59 にする。
  // 日付が未入力の場合は時刻だけ設定しても datetime-local が無効値となるため、何もしない。
  toggleBulkAllDay (event) {
    const checkbox = event.target
    const row = checkbox.closest('[data-bulk-date-row]')
    if (!row) return

    const start = row.querySelector('[data-bulk-date-input="start_at"]')
    const end = row.querySelector('[data-bulk-date-input="end_at"]')
    applyAllDay(start, end, checkbox.checked)
  }

  appendBulkDates (formData) {
    if (!this.hasBulkDatesContainerTarget) return

    const rows = this.bulkDatesContainerTarget.querySelectorAll('[data-bulk-date-row]')
    rows.forEach((row, idx) => {
      const start = row.querySelector('[data-bulk-date-input="start_at"]')?.value
      const end = row.querySelector('[data-bulk-date-input="end_at"]')?.value
      if (!start || !end) return
      // サーバは params.require(:round_bulk_settings).permit(bulk_tour_plan_dates: ...) で
      // round_bulk_settings の下を見るため、必ず round_bulk_settings[] でネストして送る。
      formData.append(`round_bulk_settings[bulk_tour_plan_dates][${idx}][start_at]`, start)
      formData.append(`round_bulk_settings[bulk_tour_plan_dates][${idx}][end_at]`, end)
    })
  }

  persistBulkInputs (inputs) {
    try {
      const snapshot = Array.from(inputs).map((input) => ({
        name: input.name,
        value: input.value,
        type: input.type,
        checked: input.type === 'checkbox' ? input.checked : null,
      }))
      window.sessionStorage.setItem(this.storageKey(), JSON.stringify(snapshot))
    } catch (_e) {
      // SecurityError / QuotaExceededError は復元できないだけで実害なし
    }
  }

  restoreBulkInputs () {
    let snapshot
    try {
      const raw = window.sessionStorage.getItem(this.storageKey())
      if (!raw) return
      snapshot = JSON.parse(raw)
    } catch (_e) {
      return
    }

    const inputsByName = new Map()
    this.bulkInputs().forEach((input) => inputsByName.set(input.name, input))

    snapshot.forEach((entry) => {
      const input = inputsByName.get(entry.name)
      if (!input) return
      if (input.type === 'checkbox') {
        input.checked = !!entry.checked
      } else if (entry.value !== '' && entry.value != null) {
        input.value = entry.value
      }
    })
  }

  bulkInputs () {
    return this.element.querySelectorAll('[data-bulk-input]')
  }

  // 同一URLへの location.href 代入では reload が発火せず connect() も再実行されないため、
  // パスが同じ場合は location.reload() を強制する。pathname が違うなら通常の navigation で良い。
  navigateAfterSuccess () {
    const target = this.successRedirectUrlValue
    const targetUrl = new URL(target, window.location.origin)
    if (window.location.pathname === targetUrl.pathname) {
      if (window.location.hash !== targetUrl.hash) {
        window.history.replaceState(null, '', target)
      }
      window.location.reload()
    } else {
      window.location.href = target
    }
  }

  async submit (url, formData) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': csrfToken,
          Accept: 'text/html',
        },
        credentials: 'same-origin',
        redirect: 'follow',
      })
      if (response.ok || response.redirected) {
        this.navigateAfterSuccess()
        return
      }
      // 422 等のエラー応答はサーバが返した本文を alert で表示する。
      // ページ遷移しないので入力中の form 状態（group_order 等）は維持される。
      const errorText = await response.text()
      window.alert(errorText || `処理に失敗しました (HTTP ${response.status})`)
    } catch (err) {
      window.alert(`通信エラー: ${err.message}`)
    }
  }
}
